import * as CURRENCIES from "@dinero.js/currencies";
import { dinero, toFormat } from "dinero.js";
import { find } from "lodash";

export const getDate = (timestamp: number | undefined): Date | null =>
  timestamp !== undefined ? new Date(timestamp * 1000) : null;

export const getTimestamp = (date: string): number => Date.parse(date) / 1000;

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const addDuration = (date: Date | null, duration: number) => {
  if (date instanceof Date) {
    return new Date(date.getTime() + duration * 1000);
  }
  return null;
};

export const getBase64Img = (file: File): Promise<string | ArrayBuffer | null | Error> =>
  // eslint-disable-next-line compat/compat
  new Promise((resolve, reject) => {
    if (!file) resolve("");
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (): void => resolve(reader.result);
    reader.onerror = (error): void => reject(error);
  });

export const minsToDuration = (mins: number | undefined): null | { hours: number; minutes: number } => {
  if (typeof mins === "undefined") return null;
  const absMins = Math.abs(mins);
  const hours = Math.floor(absMins / 60);
  const minutes = Math.floor(absMins % 60);
  if (mins < 0) {
    return {
      hours: parseInt(`-${hours}`, 10),
      minutes: parseInt(`-${minutes}`, 10),
    };
  }
  return {
    hours,
    minutes,
  };
};

export const convertAmount = (amount: number, currency: string): string => {
  const currencyObject = find(CURRENCIES, (item) => item.code === currency) || CURRENCIES.TWD;
  const d = dinero({
    amount,
    currency: currencyObject,
  });
  return toFormat(d, ({ amount: optAmount }) => `${optAmount}`);
};

export const secondsToDays = (seconds: number): number => Math.floor(seconds / (3600 * 24));

export const secondsToHours = (seconds: number): number => Math.floor(seconds / 3600);

export const getPricePerDay = (price: number, days: number): number => price / days;

export const discount = (origin: number, comparison: number): number => Math.round((1 - comparison / origin) * 100);

enum TimeUnit {
  hour = "hour",
  minute = "minute",
  second = "second",
}

const padStart = (time: number, length = 2) => time.toString().padStart(length, "0");

export const durationToTimeString = (totalSeconds: number): string => {
  const isNegative = totalSeconds < 0;

  const abrTotalSeconds = Math.abs(Math.floor(totalSeconds));
  const totalMinutes = Math.floor(abrTotalSeconds / 60);
  const seconds = abrTotalSeconds % 60;
  const minutes = totalMinutes % 60;
  const hours = Math.floor(totalMinutes / 60);

  const timeInfo = {
    [TimeUnit.hour]: {
      value: hours,
      unit: "h",
    },
    [TimeUnit.minute]: {
      value: minutes,
      unit: "m",
    },
    [TimeUnit.second]: {
      value: seconds,
      unit: "s",
    },
  };

  const timeString = Object.values(TimeUnit)
    .map((unit) => padStart(timeInfo[unit].value))
    .join(":");

  return isNegative ? `-${timeString}` : timeString;
};

/**
 * Convert Time String to timestamp
 * @param timeString - The time string to convert
 * @returns The timestamp
 * @example
 * returns 5400
 * timeStringToTimestamp("01:30:00");
 * @example
 * returns 330
 * timeStringToTimestamp("00:05:30");
 */
export const timeStringToTimestamp = (timeString: string): number => {
  const [hours, minutes, seconds] = timeString.split(":").map(Number);
  return hours * 3600 + minutes * 60 + seconds;
};
